// Manager — "Help me draft a response", CASE-LESS (PO 2026-08-06).
//
// THE PROBLEM THIS FIXES: the Maeve-First home card of the same name used to
// deep-link at the manager's most recent Open pathway, and, when they had
// none, at `/manager/cases/new` with the subtitle "drafting opens once an
// employee is added". So the one moment a manager most needs help with wording
// — the first time they sign in, having just heard something they don't know
// how to reply to — was the one moment the feature refused to run. Drafting is
// now a function in its own right, tagged to nobody.
//
// THE HYBRID (PO decision): generic first, attach a pathway if you want to.
//   - The generic drafter is the page. It always works, including on a brand
//     new account with an empty org.
//   - If the manager DOES have open pathways, they're offered above it. Picking
//     one leaves this page entirely for `/manager/cases/:id/first-response` —
//     the existing, pathway-scoped, CGC-audited Sub-mode A flow, unchanged.
//     Maeve there knows who it's about; here she cannot.
//
// So the governance model is untouched: every pathway-bearing draft still runs
// through the audited per-case endpoint. This page's endpoint
// (`POST /api/manager/maeve/draft`) accepts two closed enums and has no field
// that could carry a person's identity — see `GenericDraftRequestSchema`.
//
// Deliberately NOT a chat. The Maeve tab (`/manager/maeve`) is the
// conversational surface and is a pathway picker for the same governance
// reason; this is a form → three drafts → edit → send, which is the shape
// Journey 01 already validated for a manager acting under stress.

import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import {
  GENERIC_DRAFT_RELATIONSHIPS,
  GENERIC_DRAFT_SITUATIONS,
  type FirstResponseVariant,
  type GenericDraftRelationship,
  type GenericDraftSituation,
} from '@besideu/shared/maeve';
import { api, ApiError } from '@/lib/api.ts';
import { useCases } from '@/lib/use-cases.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Card, NavCard } from '@/components/ui/Card.tsx';
import { Field, Select } from '@/components/ui/Field.tsx';
import { DraftToneComposer } from '@/components/DraftToneComposer.tsx';

// Manager-facing labels for the closed enums the endpoint accepts. The wire
// values are stable identifiers; these are copy, and they read as the question
// the manager is actually asking themselves.
const SITUATION_LABEL: Record<GenericDraftSituation, string> = {
  first_response: "I've just heard, and I haven't said anything yet",
  no_reply_yet: "I've reached out and had no reply",
  practical_arrangements: 'I need to raise time off, pay or cover',
  checking_in: 'Some time has passed and I want to check in',
  returning_to_work: "They're coming back to work soon",
};

const RELATIONSHIP_LABEL: Record<GenericDraftRelationship, string> = {
  Parent: 'Their parent',
  Partner: 'Their partner',
  Child: 'Their child',
  Sibling: 'Their sibling',
  Grandparent: 'Their grandparent',
  Other: 'Someone else close to them',
  Unspecified: "I don't know",
};

interface DraftResponse {
  variants: FirstResponseVariant[];
  promptVersionId: string;
}

export function ManagerDraftResponse() {
  const [situation, setSituation] = useState<GenericDraftSituation>('first_response');
  const [relationship, setRelationship] = useState<GenericDraftRelationship>('Unspecified');

  const draft = useMutation({
    mutationFn: () =>
      api.post<DraftResponse>('/api/manager/maeve/draft', { situation, relationship }),
  });

  return (
    <section className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Draft a response</h1>
        <p className="mt-2 text-muted-foreground">
          Tell Maeve roughly what you&rsquo;re facing and she&rsquo;ll draft three versions you can
          edit and send from your own account. You don&rsquo;t need to have set anything up first,
          and you don&rsquo;t have to name anyone.
        </p>
      </header>

      <ExistingPathways />

      <Card className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Where are you up to?">
            {(id, descProps) => (
              <Select
                id={id}
                {...descProps}
                value={situation}
                onChange={(e) => setSituation(e.target.value as GenericDraftSituation)}
              >
                {GENERIC_DRAFT_SITUATIONS.map((s) => (
                  <option key={s} value={s}>
                    {SITUATION_LABEL[s]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label="Who died?"
            hint="Only used to pitch the wording. Nothing here identifies anyone."
          >
            {(id, descProps) => (
              <Select
                id={id}
                {...descProps}
                value={relationship}
                onChange={(e) => setRelationship(e.target.value as GenericDraftRelationship)}
              >
                {GENERIC_DRAFT_RELATIONSHIPS.map((r) => (
                  <option key={r} value={r}>
                    {RELATIONSHIP_LABEL[r]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => draft.mutate()} loading={draft.isPending}>
            {draft.data ? 'Draft again' : 'Draft a response'}
          </Button>
          {draft.isPending ? (
            <p className="text-sm text-muted-foreground" role="status">
              Maeve is writing three drafts…
            </p>
          ) : null}
        </div>

        {draft.error ? <DraftError error={draft.error} onRetry={() => draft.mutate()} /> : null}

        {draft.data ? (
          // Key on the mutation's submit timestamp so "Draft again" remounts
          // the composer — its edit buffer is seeded once from `variants`, so
          // without a fresh mount the new drafts wouldn't replace the old
          // textareas. Same reason as the pathway-scoped surface.
          <DraftToneComposer
            key={draft.submittedAt}
            variants={draft.data.variants}
            // No `onCommit` and no confirm button: there is no support pathway
            // here, so there is no `first_response_sent_at` to stamp. Offering
            // "Use this draft" would imply this had been recorded against
            // someone, which is the exact confusion this page exists to remove.
            footnote={
              <>
                Maeve doesn&rsquo;t know who this is for, so anywhere she&rsquo;s written{' '}
                <span className="font-medium text-foreground">[name]</span> is yours to fill in.
                Edit freely — the platform stores nothing of the message text.
              </>
            }
          />
        ) : null}
      </Card>

      {draft.data ? <LogAsPathway /> : null}
    </section>
  );
}

// The "attach a pathway" half of the hybrid. Renders only when the manager has
// open pathways — on a first sign-in (the case the PO raised) there are none
// and the page is purely the generic drafter, with no empty state to read past.
//
// Choosing one LEAVES this page for the pathway-scoped Sub-mode A flow rather
// than pulling that context in here: that draft is grounded in the case row,
// audited against the employee, and stamps `first_response_sent_at` on send.
// None of that can happen on a case-less endpoint, so the two must stay
// separate destinations rather than one page with a hidden mode switch.
function ExistingPathways() {
  const cases = useCases();
  const open = (cases.data?.cases ?? []).filter((c) => c.status === 'Open');
  if (open.length === 0) return null;

  return (
    <Card className="space-y-3">
      <div>
        <h2 className="text-base font-semibold">Is this about someone you already support?</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Then start from their support pathway instead — Maeve can draft from what she already
          knows, and it&rsquo;ll be recorded against their timeline.
        </p>
      </div>
      <ul className="space-y-2">
        {open.map((c) => (
          <li key={c.id}>
            <NavCard
              to={`/manager/cases/${c.id}/first-response`}
              title={c.employee.name}
              titleTone="foreground"
              subtitle={`${c.deceasedRelationship} · loss ${c.dateOfLoss}`}
            />
          </li>
        ))}
      </ul>
      <p className="text-sm text-muted-foreground">
        Otherwise, carry on below — nothing you draft here is tied to anyone.
      </p>
    </Card>
  );
}

// Offered only AFTER a draft exists. A manager who came here because they had
// nowhere to start has, by this point, a message to send and a person in mind
// — which is the natural moment to open a pathway, and the moment the touch
// point series and HR visibility actually begin. Before the draft it would be
// the same "go and create a case first" deflection this page was built to
// remove.
function LogAsPathway() {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/40 p-5">
      <h2 className="text-base font-semibold">Once you&rsquo;ve sent it</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Opening a support pathway starts the check-in schedule, lets HR see that someone is being
        supported, and gives Maeve the context to help with what comes next.
      </p>
      <Link to="/manager/cases/new" className="mt-3 inline-block">
        <Button variant="secondary" className="py-2 text-sm">
          Open a support pathway
        </Button>
      </Link>
    </div>
  );
}

// Mirrors `ManagerMaeveFirstResponse`'s error copy — same endpoints' failure
// modes (503 = no active Manager prompt, 502 = Vertex unreachable), same
// wording, so a manager doesn't get two different explanations of one outage.
function DraftError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { headline, detail } = explainDraftError(error);
  return (
    <div className="space-y-3 rounded-md border border-traffic-red/40 bg-traffic-red/5 p-4 text-sm">
      <div>
        <p className="font-medium text-traffic-red">{headline}</p>
        <p className="mt-1 text-muted-foreground">{detail}</p>
      </div>
      <Button variant="secondary" onClick={onRetry} className="py-1.5 text-sm">
        Try again
      </Button>
    </div>
  );
}

function explainDraftError(error: unknown): { headline: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.status === 503 && error.message === 'maeve_unavailable') {
      return {
        headline: "Maeve isn't available right now",
        detail:
          'A besideU administrator needs to enable the Manager prompt before drafts can be generated. Please contact your HR team.',
      };
    }
    if (error.status === 502 && error.message === 'maeve_upstream_error') {
      return {
        headline: "Couldn't reach Maeve",
        detail: 'The drafting service is temporarily unavailable. Please try again in a moment.',
      };
    }
  }
  return {
    headline: 'Something went wrong',
    detail: 'Please try again, or refresh the page if the problem persists.',
  };
}
